import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSpeech } from '../hooks/useSpeech';
import ProgressBar from '../components/ProgressBar';
import Flashcard from './Flashcard';
import { useFlashcardSession, type SessionOptions } from './useFlashcardSession';
import { useDeckRun } from './useDeckRun';
import { drillActionFor } from './drillKeys';
import { describeSetup } from './describeSetup';
import HandsFreePanel from './voice/HandsFreePanel';
import { queueSession } from '../api/pendingSessions';
import { tzOffsetMinutes } from '../config';
import type { Card, CardResult, Language, UserSettings } from '../../../shared/types';

export interface DrillProps {
  /** The deck to run. Must not be empty — the caller decides what to say if it is. */
  cards: Card[];
  language: Language;
  settings: UserSettings;
  /** What this run is, shown under "Session complete". */
  label: ReactNode;
  /** Where the ✕ and the closing link go. */
  exitTo: string;
  exitLabel: string;
  /**
   * Records the finished session. The drill does not care whether that is one
   * section's endpoint or the cross-section one — only that it resolves.
   */
  onRecord: (results: { cardId: string; result: CardResult }[]) => Promise<unknown>;
  /**
   * Anything extra for the summary's button row, rendered above the closing
   * link — the next section, say. Given `finished` so a caller can decide
   * whether to lead with it.
   */
  summaryActions?: (finished: boolean) => ReactNode;
  /** Shown under the score when the run covers more than one section. */
  runNote?: ReactNode;
  /**
   * The section this run belongs to, or null when it spans several.
   *
   * Only used to queue the session if the post fails — it is what tells the
   * retry which endpoint to send it to later. The drill itself still does not
   * care where the cards came from.
   */
  sectionId?: string | null;
  /**
   * What to call the deck in the summary — "section", "review", "session".
   * A section drill should say "30 cards left in this section", which is more
   * use than "in this run"; a review has no section to name.
   */
  deckNoun?: string;
}

/**
 * A flashcard run over whatever deck it is handed.
 *
 * Deliberately knows nothing about where the cards came from. A section drill,
 * a review of what is due and a custom session across six chapters are the same
 * loop — the same keyboard bindings, the same sitting logic, the same summary —
 * differing only in which deck they are given and where the results are posted.
 * Both of those arrive as props.
 *
 * The caller must not mount this until the deck exists. A React state
 * initializer runs on the first render, so a drill mounted over a still-loading
 * deck deals an empty queue, reports `done` at once, and drops the learner onto
 * a 0% summary having answered nothing. `FlashcardsPage` documents that bug at
 * length; the rule for every caller is the same.
 */
export default function Drill({
  cards,
  language,
  settings,
  label,
  exitTo,
  exitLabel,
  onRecord,
  summaryActions,
  runNote,
  sectionId = null,
  deckNoun = 'run',
}: DrillProps) {
  const navigate = useNavigate();
  const speech = useSpeech(language.speechTag);

  // Frozen at mount. Changing the card setup mid-drill would reorder the deck
  // under the learner, so these are read once, when the drill starts.
  const [options] = useState<SessionOptions>(() => ({
    repeatMissed: settings.flashcards.repeatMissed,
  }));
  const [shuffle] = useState(settings.flashcards.shuffle);
  const [sittingSize] = useState(settings.sessionSize);

  const run = useDeckRun(cards, shuffle, sittingSize);
  const session = useFlashcardSession(run.sitting, options);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  /** The post failed and the session is sitting in the device's retry queue. */
  const [queued, setQueued] = useState(false);
  const [handsFree, setHandsFree] = useState(false);
  /**
   * Set once "listen and repeat" has run, and never cleared for this session.
   *
   * Echo answers every card correct because there is nothing to judge — you
   * either said it along with the voice or you did not, and only you know. Those
   * answers must not reach the review schedule: a streak built from repeating
   * words after a voice would push genuinely shaky cards a month into the
   * future. Suppressing the whole session is the safe direction to fail, and it
   * is said plainly on the summary rather than silently dropped.
   */
  const [practiceOnly, setPracticeOnly] = useState(false);
  const markPracticeOnly = useCallback(() => setPracticeOnly(true), []);

  const { done, results, restart } = session;

  // Post the whole session once, when it finishes. Nothing is sent card by card,
  // so a drill never waits on the network between answers.
  useEffect(() => {
    if (!done || results.length === 0 || saved || saving || practiceOnly) return;
    setSaving(true);
    onRecord(results)
      .then(() => {
        setSaved(true);
        setSaveError(null);
      })
      .catch((err: unknown) => {
        // Losing a finished session to a dropped connection is the worst
        // outcome available here — the work is done and nothing about it is
        // recoverable by retrying later *by hand*. So it is kept on the device
        // and sent on the next page load that has a network.
        queueSession({ sectionId, results, tzOffsetMinutes: tzOffsetMinutes() });
        setQueued(true);
        setSaved(true);
        setSaveError(err instanceof Error ? err.message : 'Could not reach the server');
      })
      .finally(() => setSaving(false));
  }, [done, results, saved, saving, onRecord, practiceOnly, sectionId]);

  const startAgain = useCallback(
    (deck?: Card[]) => {
      setSaved(false);
      setSaveError(null);
      // A fresh deal is a fresh session, so the echo suppression lifts with it.
      setPracticeOnly(false);
      restart(deck);
    },
    [restart],
  );

  /** Continue the run: the next slice of cards this sitting has not covered. */
  const continueRun = useCallback(() => {
    const next = run.advance();
    if (next) startAgain(next);
  }, [run, startAgain]);

  /** Re-order the whole deck and begin the run again from the first card. */
  const restartRun = useCallback(() => {
    startAgain(run.restartRun());
  }, [run, startAgain]);

  /**
   * Speaks the card again. A no-op when audio is switched off for the card or
   * the device has no voice for the language — see useSpeech on why a wrong
   * voice is worse than silence.
   */
  const replayAudio = useCallback(() => {
    if (settings.flashcards.fields.audio === 'hidden') return;
    if (!speech.voiceAvailable || !session.current) return;
    speech.speak(session.current.target, settings.flashcards.speechRate);
  }, [speech, session.current, settings.flashcards.fields.audio, settings.flashcards.speechRate]);

  const canReplay = settings.flashcards.fields.audio !== 'hidden' && speech.voiceAvailable;

  // A whole session from the numeric keypad: 0 flips, 1 and 2 grade, 3 replays.
  // See drillKeys.ts — the bindings follow the physical key so they survive Num
  // Lock being off, which otherwise turns the pad into navigation keys.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (session.done || !session.current) return;
      // Hands-free drives the same session from the panel's own controls. Two
      // things grading one card would advance it twice.
      if (handsFree) return;
      if (e.target instanceof HTMLElement && ['INPUT', 'TEXTAREA'].includes(e.target.tagName)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      const action = drillActionFor(e, session.flipped);
      if (!action) return;
      e.preventDefault();

      if (action === 'flip') session.flip();
      else if (action === 'replay') replayAudio();
      else session.answer(action);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [session, replayAudio, handsFree]);

  // Audio configured as the only prompt, with no voice to speak it, would be a
  // blank card — so the drill is refused rather than started.
  const audioIsTheOnlyPrompt =
    settings.flashcards.fields.audio === 'prompt' &&
    Object.values(settings.flashcards.fields).filter((m) => m === 'prompt').length === 1;

  if (audioIsTheOnlyPrompt && (!speech.supported || !speech.voiceAvailable)) {
    return (
      <main className="page">
        <p className="error">
          This setup asks for audio only, but there is no {language.name} voice on this device —
          every card would be blank. Put the written form or the English on the front instead.
        </p>
        <Link to={exitTo} className="btn">
          {exitLabel}
        </Link>
      </main>
    );
  }

  if (session.done) {
    const accuracy = session.total > 0 ? Math.round((session.correct / session.total) * 100) : 0;
    // Nothing left in this run: no further sitting, and nothing just missed.
    const finished = run.remainingAfter === 0 && session.missedCards.length === 0;

    return (
      <main className="page page--narrow">
        <section className="summary">
          <h1>Session complete</h1>
          <p className="muted">{label}</p>

          <div className="summary-score">
            <span className="summary-value">{accuracy}%</span>
            <span className="summary-label">
              {session.correct} of {session.total} cards right
            </span>
          </div>

          {runNote}

          {!run.isWholeDeck && (
            <p className="muted summary-run">
              Sitting {run.sittingNumber} of {run.sittingCount} ·{' '}
              {run.remainingAfter > 0
                ? `${run.remainingAfter} cards left in this ${deckNoun}`
                : 'that is all of them covered'}
            </p>
          )}

          <ProgressBar value={accuracy} />

          {practiceOnly && (
            <p className="muted">
              Practice only — this run used listen and repeat, so it was not recorded against your
              review schedule.
            </p>
          )}
          {saving && <p className="muted">Saving your progress…</p>}
          {saved && !queued && <p className="muted">Progress saved.</p>}
          {queued && (
            <p className="muted">
              Saved on this device — {saveError?.toLowerCase()}. It will be sent the next time the
              app opens with a connection, so nothing here is lost.
            </p>
          )}

          {session.missedCards.length > 0 && (
            <div className="summary-missed">
              <h2>Still shaky</h2>
              <ul className="vocab-list">
                {session.missedCards.map((card) => (
                  <li key={card.id} className="vocab-entry">
                    <span className="vocab-target" lang={language.code}>
                      {card.target}
                    </span>
                    {card.translit && <span className="vocab-translit">{card.translit}</span>}
                    <span className="vocab-translation">{card.translation}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="summary-actions">
            {run.remainingAfter > 0 && (
              <button type="button" className="btn btn--primary btn--large" onClick={continueRun}>
                Keep going — next {Math.min(session.total, run.remainingAfter)} cards
              </button>
            )}
            {session.missedCards.length > 0 && (
              <button
                type="button"
                className={run.remainingAfter > 0 ? 'btn' : 'btn btn--primary'}
                onClick={() => startAgain(session.missedCards)}
              >
                Drill the {session.missedCards.length} you missed
              </button>
            )}
            <button type="button" className="btn" onClick={restartRun}>
              {run.isWholeDeck ? 'Run the whole deck again' : `Start this ${deckNoun} over`}
            </button>
            {summaryActions?.(finished)}
            <Link to={exitTo} className="btn">
              {exitLabel}
            </Link>
          </div>
        </section>
      </main>
    );
  }

  if (!session.current) {
    // Not reachable: the deck is non-empty and a non-empty queue always has a
    // current card. Kept so a future change fails visibly rather than blankly.
    return (
      <main className="page">
        <p className="muted state-message">Nothing left to show in this session.</p>
      </main>
    );
  }

  const dealtSoFar = session.total - session.remaining + 1;

  return (
    <main className="page page--narrow drill">
      <header className="drill-header">
        <button type="button" className="drill-exit" onClick={() => navigate(exitTo)}>
          ✕ <span>Leave</span>
        </button>
        <span className="drill-position">
          {Math.min(dealtSoFar, session.total)} / {session.total}
          {!run.isWholeDeck && (
            <span className="drill-of-deck">
              {' '}
              · {run.studiedBefore + Math.min(dealtSoFar, session.total)} of {cards.length}
            </span>
          )}
        </span>
        <span className="drill-score">
          <span className="drill-correct">{session.correct}</span>
          <span className="drill-sep">·</span>
          <span className="drill-incorrect">{session.incorrect}</span>
        </span>
      </header>

      <ProgressBar
        value={session.total > 0 ? (session.answered / (session.answered + session.remaining)) * 100 : 0}
        compact
      />

      {handsFree ? (
        <HandsFreePanel
          language={language}
          session={session}
          speechRate={settings.flashcards.speechRate}
          onPracticeOnly={markPracticeOnly}
          onStop={() => setHandsFree(false)}
        />
      ) : (
        <>
          <Flashcard
            card={session.current}
            fields={settings.flashcards.fields}
            language={language}
            speech={speech}
            speechRate={settings.flashcards.speechRate}
            autoPlayAudio={settings.flashcards.autoPlayAudio}
            flipped={session.flipped}
          />

          <div className="drill-actions">
        {!session.flipped ? (
          <button type="button" className="btn btn--primary btn--large btn--block" onClick={session.flip}>
            Show answer
            <kbd className="kbd">0 / space</kbd>
          </button>
        ) : (
          <div className="drill-grade">
            <button
              type="button"
              className="btn btn--large btn--danger grade-button"
              onClick={() => session.answer('incorrect')}
            >
              Missed it
              <kbd className="kbd">1</kbd>
            </button>
            <button
              type="button"
              className="btn btn--large btn--primary grade-button"
              onClick={() => session.answer('correct')}
            >
              Got it
              <kbd className="kbd">2</kbd>
            </button>
          </div>
        )}
          </div>

          {canReplay && (
            <button type="button" className="btn btn--block drill-replay" onClick={replayAudio}>
              Play it again
              <kbd className="kbd">3</kbd>
            </button>
          )}

          {speech.supported && (
            <button
              type="button"
              className="btn btn--block drill-handsfree"
              onClick={() => setHandsFree(true)}
            >
              Go hands-free
            </button>
          )}

          <p className="drill-setup muted">{describeSetup(settings.flashcards, language)}</p>
          <p className="drill-keys muted" aria-hidden="true">
            <kbd className="kbd">0</kbd> flip
            <kbd className="kbd">1</kbd> missed
            <kbd className="kbd">2</kbd> got it
            {canReplay && (
              <>
                <kbd className="kbd">3</kbd> replay
              </>
            )}
          </p>
        </>
      )}
    </main>
  );
}
